import { createSelector } from "reselect";
import * as R from "ramda";
import { getters } from "Modules/MultiAddColumnModal/model";
import getColumnTypes from "components/Event/Settings/Module/utils/get-column-types";

/**
 * Type guard for form object
 * @param {*} form - The form to check
 * @returns {boolean} True if form is a valid object
 */
const isFormObject = form => form !== null && typeof form === "object";

/**
 * Gets the current form based on formIndex prop or defaults to first form
 */
export const getCurrentForm = createSelector(
  getters.forms,
  (_, props) => props?.formIndex ?? 0,
  (forms, formIndex) => {
    // Ensure forms is an array and has valid indices
    if (!Array.isArray(forms)) return {};

    // Convert formIndex to number just in case
    const index =
      typeof formIndex === "string" ? parseInt(formIndex, 10) : formIndex;

    // Get the form at index or default to first form
    const form = forms[index] || forms[0] || {};
    return isFormObject(form) ? form : {};
  }
);

/**
 * Get field types for the current module
 */
export const getFieldTypes = createSelector(
  getters.moduleId,
  getters.orgId,
  getters.eventId,
  (moduleId, orgId, eventId) => {
    if (!moduleId) return [];
    try {
      return getColumnTypes(moduleId, orgId, eventId) || [];
    } catch (e) {
      console.error("Error getting column types:", e);
      return [];
    }
  }
);

/**
 * Get field groups for module
 */
export const getFieldGroups = createSelector(
  getters.fieldGroups,
  fieldGroups => fieldGroups || []
);

/**
 * Check if phone is disabled based on display type
 */
export const getIsPhoneDisabled = createSelector(getCurrentForm, form => {
  const phoneDisplayType = R.pathOr(
    null,
    ["phoneSettings", "phoneDisplayType"],
    form
  );
  return String(phoneDisplayType) === "international";
});

/**
 * Get edit field details
 */
export const getEditField = createSelector(
  getCurrentForm,
  getters.fieldId,
  getters.fields,
  (form, fieldId, fields) => {
    const moduleField = Array.isArray(fields)
      ? fields.find(field => field && field.id === fieldId)
      : null;

    if (!moduleField) return {};

    const fieldType = R.path(["settings", "isReferenceField"], moduleField)
      ? "reference"
      : R.path(["type"], moduleField);

    return {
      ...moduleField,
      type: fieldType
    };
  }
);

/**
 * Check if the field is a standard field
 */
export const getIsStandardField = createSelector(
  getters.fieldId,
  getEditField,
  (fieldId, field) => {
    if (R.isNil(fieldId)) return false;
    return R.path(["source"], field) === "standard";
  }
);

export const getErrorMessage = createSelector(getCurrentForm, form => {
  if (!isFormObject(form)) return null;
  const {
    name = "",
    type,
    lookupSettings = {},
    referenceSettings = {},
    dropdownSettings = {}
  } = form;

  if (!name || name.length === 0) {
    return "Please enter the column's name";
  } else if (type === "lookup" && !lookupSettings.moduleId) {
    return "Select a module";
  } else if (
    type === "dropdown" &&
    (!dropdownSettings?.options ||
      dropdownSettings?.options.length === 0 ||
      dropdownSettings?.options.some(option => option.value === ""))
  ) {
    return "Add options for the dropdown field";
  } else if (
    type === "dropdown" &&
    (!dropdownSettings?.options ||
      dropdownSettings?.options.length === 0 ||
      dropdownSettings?.options.every(option => option.isCorrect === false))
  ) {
    return "Mark correct option for the dropdown field";
  } else if (
    type === "reference" &&
    (!referenceSettings.lookupFieldId || !referenceSettings.referenceFieldId)
  ) {
    return "Select an existing lookup field";
  }

  return null;
});

export const getFileOptions = R.always([
  { value: "word", label: "Word" },
  { value: "excel", label: "Excel" },
  { value: "ppt", label: "PPT" },
  { value: "pdf", label: "PDF" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" }
]);

export const getSelectedFileOptions = createSelector(getCurrentForm, form =>
  R.pathOr([], ["fileSettings", "allowedFileTypes"], form)
);

export const getIsAllowMultiUpload = createSelector(getCurrentForm, form =>
  R.pathOr(false, ["fileSettings", "allowMultiple"], form)
);

export const getShowMaxLength = createSelector(getCurrentForm, form => {
  const type = R.path(["type"], form);
  return type === "text" || type === "textarea" || type === "email";
});

export const getMaxLengthFormatted = createSelector(getCurrentForm, form => {
  const maxLength = R.path(["maxLength"], form);
  return R.isEmpty(maxLength) ? 0 : parseInt(String(maxLength), 10);
});

/**
 * Check if any form has errors
 */
export const hasFormErrors = createSelector(
  getters.forms,
  state => state,
  (forms, state) => {
    if (!Array.isArray(forms)) return true;

    for (let i = 0; i < forms.length; i++) {
      const error = getErrorMessage(state, { formIndex: i });
      if (error) return true;
    }
    return false;
  }
);
